import { computed, reactive, type ComputedRef, type Ref, type UnwrapNestedRefs } from 'vue';
import type { GIMovie, GIMovieList } from 'src/models/geoItem';
import type { IMovieMetaInfoArea } from 'src/models';
import type { MoviePlayerState } from 'src/models/moviePlayer';
import { useReactiveMasters } from 'src/composables/useMasters';
import {
  searchContinuedMovies,
  preCalculateMovieVals,
  appendFirstMgisFromContinuedMovieToLastMovie,
} from 'src/lib/moviePlayerHelper';
import { dtFormat } from 'src/lib/dateHelper';

interface Deps {
  masters: ReturnType<typeof useReactiveMasters>['masters'];
  refMetaInfoArea: Ref<IMovieMetaInfoArea | undefined>;
  setCurrentMovie: (movie: GIMovie, movieIdx: number, playOffsetMsec: number) => Promise<void>;
  updateMap: () => Promise<void>;
  resetSensorChart: () => void;
  playUI: () => void;
}

interface MoviePlayerMovieListContext {
  isCurrentMovieListReady: ComputedRef<boolean>;
  isCurrentMovieListSearchTypeRoute: ComputedRef<boolean | null>;
  playerControlCurrentTimeDisp: ComputedRef<string>;
  hasContinuedMovies: ComputedRef<boolean>;
  setMoviePlayerControlDispVals: (movieList: GIMovieList) => void;
  appendContinuedMovieMgiToCurrentMovieList: () => Promise<void>;
  onPlayContinuedMovies: () => Promise<void>;
}

export function useMoviePlayerMovieList(
  state: UnwrapNestedRefs<MoviePlayerState>,
  deps: Deps,
): UnwrapNestedRefs<MoviePlayerMovieListContext> {
  const isCurrentMovieListReady = computed(() => {
    return !!(state.movieList.listSelection.currentMovieListId && state.movieList.listSelection.currentMovieList);
  });
  const isCurrentMovieListSearchTypeRoute = computed(() => {
    return state.movieList.listSelection.currentMovieList && state.movieList.listSelection.currentMovieList.searchType === 'route';
  });
  const playerControlCurrentTimeDisp = computed(() => {
    let ret = '00:00:00';
    if (!state.movieList.listSelection.currentMovieList) return ret;

    if (!isCurrentMovieListSearchTypeRoute.value) {
      // ルート検索以外の場合は撮影時刻を表示
      ret = dtFormat(state.geo.currentMovieCurrentDt, 'HH:MM:SS');
    } else {
      // ルート検索の場合は現在の経過秒数を時刻表示に直したものを表示
      let sec = state.playback.seek.current / 1000;
      const h = parseInt((sec / 3600).toString());
      sec = parseInt((sec % 3600).toString());
      const m = parseInt((sec / 60).toString());
      const s = parseInt((sec % 60).toString());
      ret = [
        ('0' + h).slice(-2),
        ('0' + m).slice(-2),
        ('0' + s).slice(-2),
      ].join(':');
    }
    return ret;
  });
  const hasContinuedMovies = computed(() => {
    return state.movieList.current.isLastMovie && state.movieList.current.continuedMovies.length > 0;
  });
  const setMoviePlayerControlDispVals = (movieList: GIMovieList) => {
    // 1つ目のmovieのplayStartOffsetMsecを0位置として扱う
    const firstMovie = movieList.movies[0];
    const firstMovieStartOffsetDiffMsec =
      firstMovie.playStartOffsetMsec === undefined || firstMovie.hardStartOffsetMsec === undefined
        ? 0
        : firstMovie.playStartOffsetMsec - firstMovie.hardStartOffsetMsec;
    // なのでseek.minは0以下の数値となる
    state.playback.seek.min = -firstMovieStartOffsetDiffMsec;
    state.playback.seek.max = (movieList.fullDurationMsec ?? 0) - firstMovieStartOffsetDiffMsec;
    state.playback.seek.current = firstMovie.playStartOffsetMsec || 0;
    state.playback.seek.step = 250; // msec
    state.movieList.timeDisp.startTimeDisp = dtFormat(movieList.hardStartTs, 'HH:MM:SS');
    state.movieList.timeDisp.endTimeDisp = dtFormat(movieList.hardEndTs, 'HH:MM:SS');
  };
  const tryGetContinuedMovies = async() => {
    if (state.movieList.current.continuedMovies.length > 0) return;

    const currentMovies = state.movieList.listSelection.currentMovieList?.movies;
    if (!currentMovies) return;

    const lastMovie = currentMovies[currentMovies.length - 1];
    state.movieList.current.continuedMovies = await searchContinuedMovies(lastMovie, deps.masters.kp.kpMapByKpUid);
  };
  const appendContinuedMovieMgiToCurrentMovieList = async() => {
    if (isCurrentMovieListSearchTypeRoute.value) return;

    await tryGetContinuedMovies();
    appendFirstMgisFromContinuedMovieToLastMovie(state.movieList.listSelection.currentMovieList, state.movieList.current.continuedMovies);
  };
  const onPlayContinuedMovies = async() => {
    if (
      state.movieList.current.isTryingToPlayAnotherMovie
      || !state.movieList.current.currentMovie
      || !state.movieList.listSelection.currentMovieList
    ) return;

    state.movieList.current.isTryingToPlayAnotherMovie = true;
    // 途中の await が例外を投げてもロックが残らないよう finally で解除する
    try {
      // 最後の動画では無くなったため、end_offset_msecを無効化する.
      state.movieList.current.currentMovie.endOffsetMsec = null;
      // 次の動画をcurrentMovieListに追加することで、同じMovieGroupと同じような操作性を得られ、
      // かつロジックも流用可能になる。
      // (currentMovieListはprops.movieListsから取得しており、
      // concatだとprops.movieListsが更新されないのでpushしておく)
      state.movieList.listSelection.currentMovieList.movies.push(...state.movieList.current.continuedMovies);
      state.movieList.current.continuedMovies = [];
      // 地図上に続きの動画までの線を描画するため、最後の動画からの続きの動画を取得する
      await tryGetContinuedMovies();
      // currentMovieListが更新されたことにより、再計算する。
      state.movieList.listSelection.currentMovieList.isPreCalculated = false;
      preCalculateMovieVals(state.movieList.listSelection.currentMovieList, deps.masters.kp.roadNameDispMap);
      setMoviePlayerControlDispVals(state.movieList.listSelection.currentMovieList);
      if (deps.refMetaInfoArea.value) {
        deps.refMetaInfoArea.value.setAllUIStateToDefault();
      }
      deps.resetSensorChart();
      const nextMovieIdx = state.movieList.current.currentMovieIdx + 1;
      const nextMovie = state.movieList.listSelection.currentMovieList.movies[nextMovieIdx];
      await deps.setCurrentMovie(
        nextMovie,
        nextMovieIdx,
        0,
      );
    } finally {
      state.movieList.current.isTryingToPlayAnotherMovie = false;
    }
    appendFirstMgisFromContinuedMovieToLastMovie(state.movieList.listSelection.currentMovieList, state.movieList.current.continuedMovies);
    // 地図更新の完了は待たずに再生を再開する（base 挙動の保存）
    void deps.updateMap();
    deps.playUI();
  };

  return reactive({
    isCurrentMovieListReady,
    isCurrentMovieListSearchTypeRoute,
    playerControlCurrentTimeDisp,
    hasContinuedMovies,
    setMoviePlayerControlDispVals,
    appendContinuedMovieMgiToCurrentMovieList,
    onPlayContinuedMovies,
  });
}
